import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { Router } from '@angular/router';
import { AbstractMerchantSupplierComponent } from 'app/core/common/abstract.merchant-supplier.component';
import { Supplier } from 'app/core/models/supplier';
import { ContractService } from 'app/core/services/contract.service';
import { MerchantService } from 'app/core/services/merchant.service';
import { NotifMessageService } from 'app/core/services/notifmessage.service';
import { PosService } from 'app/core/services/pos.service';
import { SupplierService } from 'app/core/services/supplier.service';
import { NGXLogger } from 'ngx-logger';
import { combineLatest, forkJoin, Observable, of, ReplaySubject, Subscription } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { AuthenticatedRouteGuard } from 'security/authenticatedRouteGuard.service';
import { KeycloakService } from 'security/keycloak.service';
import { tableEventChange } from '../table-utils/classes/tableChangeEvent';
import { actionType } from '../table-utils/enums/actionType';
import { columnType } from '../table-utils/enums/columnType';

@Component({
  selector: 'app-supplier-page',
  templateUrl: './supplier-page.component.html'
})
export class SupplierPageComponent extends AbstractMerchantSupplierComponent implements OnInit, OnDestroy {

  dataSubject$: ReplaySubject<any[]> = new ReplaySubject<any[]>();
  totalLength$: ReplaySubject<number> = new ReplaySubject<number>();

  isLoadingResults : boolean = true;
  isDeletedIncluded : boolean = false;

  statusItems: Array<String> = new Array<String>();

  columns: {
    data : {
      title: string;
      type: columnType
    }[],
    clickable : boolean;
    };

  pager : {page : number,
    sort : string,
    size : number
  } = {
    page: 0,
    sort: "createdDate,DESC",
    size: 20
  };

  criteria : Supplier = new Supplier();

  columnPropertyMapping = {
    "SUPPLIER" : "name",
    "EXTERNAL_REF" : "externalRef",
    "CREATION_DATE" : "createdDate",
    "STATUS" : "deleted",
    "ACTIONS" : "actions"
  };

  searchForm : FormGroup = new FormGroup({
    name: new FormControl(),
    id: new FormControl(),
    externalRef: new FormControl(),
    deleted: new FormControl()
  });

  modalResponseSubscription : Subscription;
  statusSubscription: Subscription;

  isSearchBySupplier : boolean = true;

  constructor(
    private roleGuard: AuthenticatedRouteGuard,
    private notifService: NotifMessageService,
    private keycloakService: KeycloakService,
    protected supplierService: SupplierService,
    protected merchantService: MerchantService,
    protected posService: PosService,
    protected log: NGXLogger,
    private contractService: ContractService,
    public router: Router
  ) { super(supplierService, merchantService, posService, log, router); }

  ngOnInit() {
    this.columns = {
      data : [
      { title: "SUPPLIER", type: columnType.text },
      { title: "EXTERNAL_REF", type: columnType.text },
      { title: "CREATION_DATE", type: columnType.date },
      { title: "STATUS", type: columnType.chip },
      { title: "ACTIONS", type: columnType.actions }
    ],
    clickable : true
    };

    this.statusItems = this.getMerchantSupplierStatus();
    this.criteria["deleted"] = 'false';
    this.keycloakService.rolesObservable.subscribe(roles => {
      if (roles.length !== 0) {
        if (this.roleGuard.isEnabled('SUPPLIER_READ')) {
          this.loadData();
        }
      }
    });

    this.statusSubscription = this.searchForm.get("deleted").valueChanges.subscribe(val => {
      if (val === "ALL" || val === null){
      }
      else if (val ==="DISABLED"){
        this.criteria["deleted"] = 'true';
      }
      else if (val === "ACTIVE"){
        this.criteria["deleted"]= 'false';
      }
    });

    this.search();
  }

  ngOnDestroy() {
    super.closeSubscribtions();
  }

  formatSupplier(supplier : Supplier) {
    const actions = [];

    if (this.roleGuard.isEnabled('SUPPLIER_EDIT') && !supplier.deleted) {
      actions.push({
        "name" : "EDIT",
        "type" : actionType.navigation,
        "value": `/service/supplier/edit/${supplier.id}`
      });
    }
    if (this.roleGuard.isEnabled('SUPPLIER_READ')) {
      actions.push({
        "name" : "DETAILS",
        "type" : actionType.navigation,
        "value": `/service/supplier/${supplier.id}`
      });
    }
    if (this.roleGuard.isEnabled('SUPPLIER_DELETE') && !supplier.deleted) {
      actions.push({
        "name" : "DELETE",
        "type" : actionType.eventEmitter,
        "value": supplier.id
      })
    }

    return {
      id: supplier.id,
      SUPPLIER: supplier.name,
      EXTERNAL_REF: supplier.externalRef,
      CREATION_DATE: supplier.createdDate,
      STATUS: supplier.deleted ? "DISABLED" : "ENABLED",
      deleted: supplier.deleted,
      ACTIONS: actions
    }
  }

  loadData(isLoadingSearch : boolean = false) : void {
    this.isLoadingResults = true;
    this.supplierService
    .findByCriteria(this.criteria, this.pager, false)
    .pipe(
      tap(suppliers => this.totalLength$.next(suppliers.totalElements)),
      map(suppliers => {
        if (!suppliers) {
          this.isLoadingResults = false;
          return [];
        }
        if (suppliers.content.length === 0) {
          if (isLoadingSearch) {
            this.notifService.notifyInfo('EMPTY_RESULT');
          }
          return [];
        }
        return suppliers.content.map(supplier =>this.formatSupplier(supplier));
      }),
    ).subscribe(suppliers =>  {
      this.isLoadingResults = false;
      this.dataSubject$.next(suppliers);
    })
  }

  deleteButton(id) {
    this.supplierService.getSupplierById(id).subscribe(supplier => {
      this.notifService.emitModalWithDetails("DELETE_SUPPLIER", [supplier.name,"SUPPLIER_DELETION","DELETE_SUPPLIER_MSG","DELETE_FIDELITY_PROGRAM_SUPPLIER"]);

      this.modalResponseSubscription = this.notifService.modalResponseSubject$.subscribe(resp => {
        if (resp) {
          forkJoin(
            this.contractService.deleteContractBySupplierId(id),
            this.supplierService.deleteSupplier(id)
          )
            .subscribe(
              () => {
                this.loadData();
                this.notifService.notifySuccess('SUPPLIER_DELETED');
                this.modalResponseSubscription.unsubscribe();
              }
            );
        } else {
          this.modalResponseSubscription.unsubscribe();
        }
      });
    });
  }

  includeDeleted(event : MatCheckboxChange) : void {
    this.isDeletedIncluded = event.checked;
    this.loadData();
  }

  tableChange(event: tableEventChange) {
    const sortProperty = this.columnPropertyMapping[event.sortEvent.active];
    const sortDirection = event.sortEvent.direction.toUpperCase();
    this.pager = {
      page: event.pageIndex,
      sort: sortDirection !== "" && sortProperty ? [sortProperty, sortDirection].join(",") : "lastModifiedDate,DESC",
      size: event.pageSize
    }
    this.loadData();
  }

  search() : void {
    this.criteria = new Supplier();
    for (let field in this.searchForm.value) {
        if (!this.searchForm.value[field]) {
          delete this.searchForm.value[field];
        } else if (field != 'deleted') {
          this.criteria[field] = this.searchForm.value[field];
        }
    }
    this.loadData(true);
  }

  details(id : number) : void{
    this.router.navigate([`/service/supplier/${id}`]);
  }

  clearSearch(): void {
    this.searchForm.reset();
    this.criteria = new Supplier();
    this.loadData();
  }

  searchByMerchant() {
    this.isLoadingResults = true;
    const merchantId = parseInt(this.searchForm.value["id"]);
    const merchantName = this.searchForm.value["name"];
    forkJoin([
      this.getMerchantIdsByMerchantName(merchantName),
      this.getMerchantIdsByMerchantId(merchantId)
    ]).pipe(switchMap(
      merchantIds => {
          const allMerchantIds = [...new Set([].concat.apply([], merchantIds))];
          if (allMerchantIds.length === 0) {
            return of([]);
          } else {
            const supplierIds$ = allMerchantIds.map(merchantId =>
              this.contractService.findByCriteria({merchantId : merchantId}).pipe(map(
              contracts => contracts.content.map(contract => contract.supplierId)
            )));
            return combineLatest(supplierIds$);
          }
      }
    ),
    switchMap(
      supplierIds => {
        const allSupplierIds = [...new Set([].concat.apply([], supplierIds))];
        return this.supplierService.getSuppliersById(allSupplierIds).pipe(
            tap((suppliers : Array<Supplier>) => this.totalLength$.next(suppliers.length)),
            map((suppliers : Array<Supplier>) => {
            if (!suppliers || suppliers.length === 0) {
              return [];
            }
            return suppliers.map(supplier =>this.formatSupplier(supplier));
        }))
        })
    ).subscribe(suppliers =>  {
      this.isLoadingResults = false;
      if (suppliers.length === 0) {
        this.notifService.notifyInfo('EMPTY_RESULT');
      }
      this.dataSubject$.next(suppliers);
    })
  }

  getMerchantIdsByMerchantName(merchantName: string) : Observable<Array<number>> {
    if (!merchantName || merchantName.length === 0) {
      return of([]);
    } else {
      return this.merchantService.findByCriteria({name : merchantName}, {}, false).pipe(map(
        merchants => merchants.content.map(merchant => merchant.id))
      );
    }
  }

  getMerchantIdsByMerchantId(merchantId: number) : Observable<Array<number>> {
    if (!merchantId) {
      return of([]);
    } else {
      return this.merchantService.findByCriteria({id : merchantId}, {}, false).pipe(map(
        merchants => merchants.content.map(merchant => merchant.id))
      );
    }
  }
}
